/**
 * Deliberately lean runtime-config for the support console -- unlike
 * `@shared/config/runtime-config.ts` (the operator SPA's), this has NO
 * `sites`/`gatewayRegistrySeed`/`taskCatalogSeed`/`featureFlags`
 * fields: this app has no per-site static config at all (site is
 * always a query filter against `/support/**`, never a bootstrap
 * concept -- see `backend/api_server/api/support.py`'s own docstring
 * on that same point). Pulling in the operator SPA's full
 * `RuntimeConfig` type graph just to reach `authMode`/`zitadel` would
 * be the wrong kind of coupling.
 */
import type { AuthMode } from '@shared/auth/types';

export interface ConsoleZitadelConfig {
  issuer: string;
  clientId: string;
  scopes: string[];
  redirectUri: string;
  postLogoutRedirectUri: string;
  audience?: string;
}

export interface ConsoleRuntimeConfig {
  authMode: AuthMode;
  env: 'local' | 'staging' | 'production';
  zitadel: ConsoleZitadelConfig;
  /** Always Envoy's `/api/v1/support/**` front door -- never the
   * support container directly, so edge-authz's role check is never
   * bypassed (see vite.config.ts's dev proxy comment for the same
   * rule in local dev). */
  apiBaseUrl: string;
  /**
   * Grafana origin for Explore deep-links (plan, Pane 2: "deep-link
   * into Grafana Explore with a prefilled LogQL query, time range, and
   * tenant rather than rebuilding a log browser. The console owns
   * triage and action; Grafana owns exploration.").
   *
   * OPTIONAL by design: Grafana is bound to loopback
   * (`127.0.0.1:3001` in docker-compose.yml), so a console served from
   * anywhere other than the operator's own machine cannot reach it.
   * When unset the console hides the deep-link entirely rather than
   * rendering a button that leads nowhere.
   */
  grafanaBaseUrl?: string;
}

let cached: ConsoleRuntimeConfig | null = null;

export function setRuntimeConfig(cfg: ConsoleRuntimeConfig): void {
  cached = cfg;
}

export function getRuntimeConfig(): ConsoleRuntimeConfig {
  if (!cached) {
    throw new Error('Runtime config not loaded. Call loadRuntimeConfig() during bootstrap.');
  }
  return cached;
}

export function getOptionalRuntimeConfig(): ConsoleRuntimeConfig | null {
  return cached;
}

function isNonEmptyString(v: unknown): v is string {
  return typeof v === 'string' && v.length > 0;
}

export function validateRuntimeConfig(cfg: ConsoleRuntimeConfig): void {
  const errors: string[] = [];
  if (!cfg || typeof cfg !== 'object') {
    throw new Error('Invalid runtime-config.json:\n - config root must be an object');
  }
  if (cfg.authMode !== 'zitadel' && cfg.authMode !== 'mock') {
    errors.push(`authMode must be 'zitadel' or 'mock' (got '${String(cfg.authMode)}')`);
  }
  // Same rule as the operator SPA's own validator: a leaked
  // authMode=mock config must never be usable outside env=local.
  if (cfg.authMode === 'mock' && cfg.env !== 'local') {
    errors.push(`authMode=mock is not permitted when env='${cfg.env}' (only env='local')`);
  }
  if (!isNonEmptyString(cfg.apiBaseUrl)) {
    errors.push('apiBaseUrl must be a non-empty string');
  }
  if (cfg.authMode === 'zitadel') {
    if (!isNonEmptyString(cfg.zitadel?.issuer)) errors.push('zitadel.issuer must be a non-empty string');
    if (!isNonEmptyString(cfg.zitadel?.clientId)) errors.push('zitadel.clientId must be a non-empty string');
  }
  if (errors.length > 0) {
    throw new Error(`Invalid runtime-config.json:\n - ${errors.join('\n - ')}`);
  }
}

export async function loadRuntimeConfig(): Promise<ConsoleRuntimeConfig> {
  const resp = await fetch('/config/runtime-config.json', { cache: 'no-store' });
  if (!resp.ok) {
    throw new Error(`Failed to load runtime-config.json: HTTP ${resp.status}`);
  }
  const cfg = (await resp.json()) as ConsoleRuntimeConfig;
  validateRuntimeConfig(cfg);
  setRuntimeConfig(cfg);
  return cfg;
}
